import React, { useCallback, useState } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import {
  BodyText as GarnishBodyText,
  Button,
  switchcase,
  theme,
  useResponsive,
  VStack,
} from '@sweetgreen/garnish';

import {
  AccountScreenBorderedSection,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  PaymentMethodListItem,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import type {
  SweetpassBillingHistorySectionProps,
  SweetpassCancellationSectionProps,
  SweetpassMembershipLevelSectionProps,
  SweetpassPaymentMethodsSectionProps,
  useSweetpassMembershipContent,
} from './hooks';
import {
  ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION,
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION,
  ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION,
} from './hooks';

// ––– CONTENT –––––––––––––––––––––––––––––––––

export const SweetpassMembershipScreenContent = (
  props: SweetpassMembershipScreenContentProps,
) => {
  const { testID, sweetpassMembershipContent } = props;
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  return (
    <>
      <AccountScreenTitle
        testID={testID?.concat('.title')}
        visibility="COMPACT"
        title={t('account.sweetpass-membership.title')}
      />
      <VStack testID={testID} customSpacing={match([theme.spacing['6'], 0])}>
        {sweetpassMembershipContent?.map((section) =>
          switchcase({
            [ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION]: (
              <MembershipLevelSection
                key="membership-level-section"
                testID={testID?.concat('.membership-level-section')}
                {...(section as SweetpassMembershipLevelSectionProps)}
              />
            ),
            [ACCOUNT_SWEETPASS_CANCELLATION_SECTION]: (
              <CancellationSection
                key="cancellation-section"
                testID={testID?.concat('.cancellation-section')}
                {...(section as SweetpassCancellationSectionProps)}
              />
            ),
            [ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION]: (
              <PaymentMethodsSection
                key="payment-methods-section"
                testID={testID?.concat('.payment-methods-section')}
                {...(section as SweetpassPaymentMethodsSectionProps)}
              />
            ),
            [ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION]: (
              <BillingHistorySection
                key="billing-history-section"
                testID={testID?.concat('.billing-history-section')}
                {...(section as SweetpassBillingHistorySectionProps)}
              />
            ),
          })(null as unknown as React.ReactElement)(section.name ?? ''),
        )}
      </VStack>
    </>
  );
};

// ––– Section: MEMBERSHIP LEVEL –––––––––––––––––––––––––––––––––

const MembershipLevelSection = (
  props: SweetpassMembershipLevelSectionProps,
) => {
  const { testID, title, headingText, bodyText, cta } = props;

  const [isLoading, setLoading] = useState(false);

  const handlePressCta = useCallback(() => {
    if (!cta) return;
    setLoading(true);
    cta.handler().finally(() => {
      setLoading(false);
    });
  }, [cta]);

  return (
    <Section testID={testID} content="plain" title={title}>
      <HeadingText testID={testID?.concat('.heading')}>
        {headingText}
      </HeadingText>
      <BodyText testID={testID?.concat('.body')}>{bodyText}</BodyText>
      {cta ? (
        <Button
          testID={testID?.concat('.cta')}
          style={styles.cta}
          palette={cta.palette}
          size="large"
          accessibilityLabel={cta.accessibilityLabel}
          onPress={handlePressCta}
          isLoading={isLoading}
        >
          {cta.title}
        </Button>
      ) : null}
    </Section>
  );
};

// ––– Section: CANCELLATION –––––––––––––––––––––––––––––––––

const CancellationSection = (props: SweetpassCancellationSectionProps) => {
  const { testID, title, bodyText, cta } = props;

  const [isCancelling, setCancelling] = useState(false);

  const handlePressCta = useCallback(() => {
    setCancelling(true);
    cta.handler().finally(() => {
      setCancelling(false);
    });
  }, [cta]);

  return (
    <Section testID={testID} content="plain" title={title}>
      <HeadingText testID={testID?.concat('.body')}>{bodyText}</HeadingText>
      {cta ? (
        <Button
          testID={testID?.concat('.cta')}
          style={styles.cta}
          palette={cta.palette}
          size="large"
          accessibilityLabel={cta.accessibilityLabel}
          onPress={handlePressCta}
          isLoading={isCancelling}
        >
          {cta.title}
        </Button>
      ) : null}
    </Section>
  );
};

// ––– Section: PAYMENT METHODS –––––––––––––––––––––––––––––––––

const PaymentMethodsSection = (props: SweetpassPaymentMethodsSectionProps) => {
  const { testID, title, addItem, items } = props;

  const handlePressAdd = useCallback(() => {
    addItem.handler();
  }, [addItem]);

  return (
    <Section testID={testID} content="list" title={title}>
      <ActionList>
        {items.map((item) => (
          <PaymentMethodListItem
            key={item.id}
            testID={testID?.concat(
              `.edit-payment.${item.title?.split(' ').join('-') ?? item.id}`,
            )}
            iconName={item.icon}
            title={item.title}
            subtitle={item.subtitle}
            tag={item.tag}
            notice={item.notice}
            accessibilityLabel={item.accessibilityLabel}
            accessibilityHint={item.accessibilityHint}
            onPress={() => {
              item.handler();
            }}
          />
        ))}
        <PaymentMethodListItem
          testID={testID?.concat('.edit-payment.new')}
          iconName={addItem.icon}
          title={addItem.title}
          accessibilityLabel={addItem.accessibilityLabel}
          accessibilityHint={addItem.accessibilityHint}
          onPress={handlePressAdd}
        />
      </ActionList>
    </Section>
  );
};

// ––– Section: BILLING HISTORY –––––––––––––––––––––––––––––––––

const BillingHistorySection = (props: SweetpassBillingHistorySectionProps) => {
  const { testID, title, viewItem } = props;

  const handlePressView = useCallback(() => {
    viewItem.handler();
  }, [viewItem]);

  return (
    <Section testID={testID} content="list" title={title}>
      <ActionList>
        <PaymentMethodListItem
          testID={testID?.concat('.full-history')}
          title={viewItem.title}
          accessibilityLabel={viewItem.accessibilityLabel}
          accessibilityHint={viewItem.accessibilityHint}
          iconName={viewItem.icon}
          onPress={handlePressView}
        />
      </ActionList>
    </Section>
  );
};

// ––– Section Wrapper –––––––––––––––––––––––––––––––––

const Section = ({ testID, content, title, children }: SectionProps) => {
  const { currentBreakpoint } = useResponsive();

  return (
    <>
      <AccountScreenSectionTitle
        testID={testID?.concat('.title')}
        title={title}
      />
      <AccountScreenSection
        testID={testID}
        style={currentBreakpoint.isXS ? styles.mobileGrow : null}
      >
        <AccountScreenBorderedSection
          style={
            content === 'list'
              ? styles.listBorderedSection
              : styles.plainBorderedSection
          }
        >
          {children}
        </AccountScreenBorderedSection>
      </AccountScreenSection>
    </>
  );
};

type SectionProps = {
  readonly title: string;
  readonly content: 'plain' | 'list';
} & Pick<ViewProps, 'children' | 'testID'>;

// ––– Action List Wrapper ––––––––––––––––––––––––––––––––––––

const ActionList = ({ testID, children }: ActionListProps) => (
  <VStack
    testID={testID}
    customSpacing={0}
    divider={true}
    dividerColor={theme.colors.LIGHT}
  >
    {children}
  </VStack>
);

type ActionListProps = Pick<ViewProps, 'children' | 'testID'>;

// ––– Text Wrappers ––––––––––––––––––––––––––––––––––––

const HeadingText = ({ testID, children }: TextProps) => (
  <GarnishBodyText testID={testID} size={3} style={styles.headingText}>
    {children}
  </GarnishBodyText>
);

const BodyText = ({ testID, children }: TextProps) => (
  <GarnishBodyText testID={testID} size={4} style={styles.bodyText}>
    {children}
  </GarnishBodyText>
);

type TextProps = Pick<ViewProps, 'children' | 'testID'>;

// ––– Style –––––––––––––––––––––––––––––––––

const styles = StyleSheet.create({
  mobileGrow: {
    flexGrow: 0,
  },
  plainBorderedSection: {
    paddingVertical: theme.spacing['6'],
  },
  listBorderedSection: {
    paddingVertical: 0,
  },
  headingText: {
    color: theme.colors.GREEN_1,
  },
  bodyText: {
    color: theme.colors.NEUTRAL_2,
    marginTop: theme.spacing['1'],
  },
  cta: {
    marginTop: theme.spacing['6'],
  },
});

type SweetpassMembershipScreenContentProps = {
  readonly sweetpassMembershipContent: ReturnType<
    typeof useSweetpassMembershipContent
  >['sweetpassMembershipContent'];
} & ViewProps;
